/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

// import { RichText, RichTextComponent, Text } from '@msdyn365-commerce/core';
import MsDyn365, { ICookieValue, Text } from '@msdyn365-commerce/core';
import * as React from 'react';
import { IDobbiesClubRenewalData } from './dobbies-club-renewal.data';
import { IDobbiesClubRenewalProps } from './dobbies-club-renewal.props.autogenerated';
import { setDateFormat } from '../../themes/dobbies/utilities/formatDate';
export interface IDobbiesClubRenewalViewProps extends IDobbiesClubRenewalProps<IDobbiesClubRenewalData> {
    isOpen: boolean;
    className: string;
    heading: JSX.Element;
    clubRenewalCTA: JSX.Element;
    clubRenewalMessage: JSX.Element;
    onCloseModal?(): void;
}

interface IPopupModalState {
    isModalOpen: boolean;
    popupReady: boolean;
    daysLeft: number;
    expiryDate: string;
    customerDDAttribute?: string;
    activeTier?: string;
}

export type popupModalCookie = {
    name: string;
    age: number | undefined;
};

/**
 *
 * DobbiesClubRenewal component
 * @extends {React.PureComponent<IDobbiesClubRenewalProps<IDobbiesClubRenewalData>, IPopupModalState>}
 */
class DobbiesClubRenewal extends React.Component<IDobbiesClubRenewalProps<IDobbiesClubRenewalData>, IPopupModalState> {
    private _cookie: popupModalCookie;
    private _delayStarted: boolean = false;

    constructor(props: IDobbiesClubRenewalProps<IDobbiesClubRenewalData>) {
        super(props);
        this._closeModal = this._closeModal.bind(this);
        this.state = {
            isModalOpen: false,
            popupReady: false,
            daysLeft: 0,
            expiryDate: '',
            customerDDAttribute: '',
            activeTier: ''
        };
        this._cookie = {
            name: 'Dobbies-club-renewal-cookie',
            age: props.config.remindTime || undefined
        };
    }

    public componentDidMount(): void {
        const highestActiveLoyaltyCardTier = this.props.data.loyaltyCard.result?.LoyaltyGroups![0].HighestActiveLoyaltyCardTier;
        const activeTier = this.props.data.loyaltyCard.result?.LoyaltyGroups![0].LoyaltyCardTiers!.find(
            (card: any) => card.RecordId === highestActiveLoyaltyCardTier
        );
        this.getCustomerDDAttribute();
        this.getLoyaltyRemainingDays();
        this.setState({
            isModalOpen: true,
            activeTier: activeTier?.TierId
        });
    }

    public render(): JSX.Element | null {
        if (this._cookieExist(this._cookie.name)) {
            return null;
        }
        const daysLimit = this.props.context.app.config.clubPlusRenewalDays || 30;
        if (
            !this.props.context.request.user.isAuthenticated ||
            this.state.daysLeft > daysLimit ||
            this.state.activeTier !== 'Club Plus' ||
            (this.state.customerDDAttribute !== 'None' && this.state.customerDDAttribute !== 'DDCAN')
        ) {
            return null;
        }

        const { isModalOpen } = this.state;
        const { className, heading, clubRenewalCTA, clubRenewalMessage } = this.props.config;
        {
            this._startDelay();
        }
        const viewProps = {
            ...this.props,
            ...this.state,
            className: className,
            isOpen: isModalOpen,
            heading: heading && this._renderHeading,
            clubRenewalCTA: clubRenewalCTA && this._renderClubRenewalCTA,
            clubRenewalMessage: clubRenewalMessage && this._renderClubRenewalMessage,
            onCloseModal: this._closeModal
        };
        if (!this.state.popupReady) {
            return null;
        }

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private getCustomerDDAttribute = () => {
        const customer = this.props.data.customerInformation.result!;
        const customerAttr = customer?.Attributes?.filter((attr: any) => {
            return attr.Name === 'Direct Debit';
        });
        if (customerAttr) {
            this.setState({
                customerDDAttribute: customerAttr[0].AttributeValue?.StringValue
            });
        }
    };
    private getLoyaltyRemainingDays = () => {
        const loyaltyGroup =
            this.props.data.loyaltyCard?.result?.LoyaltyGroups &&
            this.props.data.loyaltyCard?.result?.LoyaltyGroups[0].LoyaltyCardTiers?.find(tier => {
                return tier.TierId === 'Club Plus';
            });

        const validToDate = loyaltyGroup && loyaltyGroup.ValidTo;

        const endDate = new Date(validToDate ?? '');
        const today = new Date();
        const timeDifference = endDate.getTime() - today.getTime() + 24 * 60 * 60 * 1000;
        const remainingDays = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));

        if (remainingDays) {
            this.setState({ daysLeft: remainingDays });
        }

        const formattedDate = setDateFormat(endDate.toLocaleDateString('en-GB'));
        formattedDate && this.setState({ expiryDate: formattedDate });
    };

    // Delays the modal to show after specific time span as per delay config
    private _startDelay(): void {
        if (!this._delayStarted) {
            const delayTime = this.props.config.delay || 0;
            this._delayStarted = true;
            MsDyn365.isBrowser &&
                setTimeout(() => {
                    this.setState({ popupReady: true }, () => {});
                }, delayTime * 1000);
        }
    }
    private _cookieExist(name: string): boolean {
        const { cookies } = this.props.context.request;
        // if cookie exists, don't display
        if ((cookies.get as (name: string, isEssential: boolean) => ICookieValue<string>)(name, true).status === 'FOUND') {
            return true;
        }

        return false;
    }
    private _setCookie(name: string, deltaDaysFromToday: number): void {
        const { cookies } = this.props.context.request;
        const expirationDate = new Date(Date.now());
        expirationDate.setTime(expirationDate.getTime() + deltaDaysFromToday * 24 * 60 * 60 * 1000);

        cookies.set(name, false, {
            expires: deltaDaysFromToday ? expirationDate : null,
            isEssential: true
        } as {});
    }
    // method to close modal, reset states and set cookies
    private _closeModal(): void {
        const remindTime = this.props.config.remindTime || 0;
        this._setCookie(this._cookie.name, remindTime);

        this.setState({
            isModalOpen: false,
            popupReady: false,
            daysLeft: 0,
            customerDDAttribute: '',
            activeTier: ''
        });
    }
    // Heading for Club renewal popup
    private get _renderHeading(): JSX.Element {
        const { heading } = this.props.config;
        return heading ? <Text className='dobbies-club-renewal-popup__title' tag={heading.tag || 'h2'} text={heading.text} /> : <></>;
    }
    // Club renewal  CTA
    private get _renderClubRenewalCTA(): JSX.Element {
        const { clubRenewalCTA } = this.props.config;
        return clubRenewalCTA ? (
            <a className='dobbies-club-renewal__cta' href={clubRenewalCTA.linkUrl.destinationUrl}>
                {clubRenewalCTA.linkText}
            </a>
        ) : (
            <></>
        );
    }
    // Message  for Club renewal
    private get _renderClubRenewalMessage(): JSX.Element {
        const { clubRenewalMessage } = this.props.config;
        return <p className='dobbies-club-renewal__message'>{clubRenewalMessage?.replace('{date}', this.state.expiryDate)}</p>;
    }
}

export default DobbiesClubRenewal;
